import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import axios from 'axios';

/**
 * OrgRoute — wraps org-scoped pages to enforce:
 * 1. User must be logged in
 * 2. URL must contain an org slug
 * 3. User must be a member of that specific org
 * 4. For admin pages, user must have admin role
 * 
 * Props:
 *   children — the page component to render
 *   requireAdmin — if true, requires admin/moderator/insights_viewer role
 */
function OrgRoute({ children, requireAdmin = false }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const { orgSlug: myOrgSlug } = useOrg();
  const [status, setStatus] = useState('loading'); // loading, authorized, unauthorized, no-slug, wrong-org

  useEffect(() => {
    if (!user) {
      setStatus('unauthorized');
      return;
    }
    if (!slug) {
      setStatus('no-slug');
      return;
    }
    checkMembership();
  }, [user, slug]);

  const checkMembership = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get('/api/org/onboard/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const memberships = res.data.memberships || [];
      const match = memberships.find(m => m.org_slug === slug);

      if (!match) {
        setStatus('wrong-org');
        return;
      }

      if (requireAdmin && !['admin', 'moderator', 'insights_viewer'].includes(match.role)) {
        setStatus('unauthorized');
        return;
      }

      setStatus('authorized');
    } catch (err) {
      setStatus('unauthorized');
    }
  };

  if (status === 'loading') {
    return <div style={styles.center}><p>Loading...</p></div>;
  }

  if (status === 'unauthorized') {
    const returnTo = encodeURIComponent(window.location.pathname);
    return <Navigate to={`/login?returnTo=${returnTo}`} />;
  }

  if (status === 'no-slug') {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2>Organization Required</h2>
          <p>Please access this page through your organization's URL.</p>
        </div>
      </div>
    );
  }

  if (status === 'wrong-org') {
    // If user belongs to a different org, redirect them to their own org's dashboard
    if (myOrgSlug) {
      return (
        <div style={styles.center}>
          <div style={styles.card}>
            <h2>Access Denied</h2>
            <p>You are not a member of this organization.</p>
            <button onClick={() => window.location.href = `/org/${myOrgSlug}/dashboard`} style={styles.btn}>
              Go to Your Organization →
            </button>
          </div>
        </div>
      );
    }
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2>Access Denied</h2>
          <p>You are not a member of this organization.</p>
          <button onClick={() => window.location.href = '/'} style={styles.btn}>Go Home</button>
        </div>
      </div>
    );
  }

  return children;
}

const styles = {
  center: { minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f3f4f6' },
  card: { backgroundColor: '#fff', borderRadius: '12px', padding: '40px', textAlign: 'center', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  btn: { marginTop: '16px', padding: '10px 24px', backgroundColor: '#7C3AED', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default OrgRoute;
